import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Coins, Crown, Sparkles, Trophy, Flame, Heart, Folder, Loader2, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import TipJar from "@/components/TipJar";

const BADGES = [
  { id: "botanist", title: "Green Thumb", desc: "Scan 10 plants", icon: "🌿" },
  { id: "zoologist", title: "Safari Master", desc: "Identify 5 animals", icon: "🐾" },
  { id: "polyglot", title: "Linguist", desc: "Translate 15 signs", icon: "🈯" },
  { id: "chef", title: "Iron Chef", desc: "Cook 5 recipes", icon: "👨‍🍳" },
];

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const [tipOpen, setTipOpen] = useState(false);
  const [cols, setCols] = useState([]);
  const [loadingCols, setLoadingCols] = useState(true);
  const [newName, setNewName] = useState("");

  const loadCols = async () => {
    setLoadingCols(true);
    try { const r = await api.get("/collections"); setCols(r.data); }
    catch { /* ignore */ } finally { setLoadingCols(false); }
  };
  useEffect(() => { if (user) loadCols(); }, [user]);

  const createFolder = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    try { const r = await api.post("/collections", { name }); setCols((c) => [...c, r.data]); setNewName(""); toast.success("Folder created"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
  };
  const removeFolder = async (id) => {
    if (!confirm("Delete this folder? Scans inside will remain in History.")) return;
    try { await api.delete(`/collections/${id}`); setCols((c) => c.filter((x) => x.id !== id)); toast.success("Folder deleted"); }
    catch { toast.error("Delete failed"); }
  };

  if (!user) return null;
  const xpToNext = 100 - (user.xp % 100);
  const progress = (user.xp % 100);

  return (
    <div className="min-h-screen pb-28 pt-6 px-4 max-w-md mx-auto">
      <div className="jx-glass rounded-3xl p-5">
        <div className="flex items-center gap-3">
          <div className="w-16 h-16 rounded-2xl jx-shutter flex items-center justify-center font-heading text-2xl font-black text-slate-950">
            {user.username[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-xl font-bold" data-testid="profile-username">{user.username}</h1>
            <p className="text-slate-400 text-xs truncate">{user.email}</p>
            <div className="mt-1 flex items-center gap-2 text-xs flex-wrap">
              {user.elite && <span className="px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/40 text-amber-300 inline-flex items-center gap-1"><Crown size={11} /> Elite</span>}
              {user.supporter && <span className="px-2 py-0.5 rounded-full bg-rose-500/10 border border-rose-400/40 text-rose-300 inline-flex items-center gap-1"><Heart size={11} fill="currentColor" /> Supporter</span>}
              {user.role === "admin" && <Link to="/admin" className="px-2 py-0.5 rounded-full bg-cyan-400/10 border border-cyan-400/40 text-cyan-300">Admin</Link>}
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl bg-white/5 border border-white/10 p-2">
            <p className="font-mono-data text-[10px] uppercase text-slate-500">Level</p>
            <p className="font-heading text-2xl font-black">{user.level}</p>
          </div>
          <div className="rounded-xl bg-white/5 border border-white/10 p-2">
            <p className="font-mono-data text-[10px] uppercase text-slate-500">XP</p>
            <p className="font-heading text-2xl font-black text-cyan-300">{user.xp}</p>
          </div>
          <div className="rounded-xl bg-white/5 border border-white/10 p-2">
            <p className="font-mono-data text-[10px] uppercase text-slate-500">Coins</p>
            <p className="font-heading text-2xl font-black text-amber-300">{user.coins}</p>
          </div>
        </div>

        <div className="mt-3">
          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-[10px] font-mono-data uppercase text-slate-500 mt-1">{xpToNext} XP to level {user.level + 1}</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <Link to="/coins" data-testid="profile-coins-cta" className="jx-glass rounded-2xl p-3 active:scale-95">
          <Coins size={20} className="text-amber-300" />
          <p className="mt-1 font-heading font-semibold text-sm">Coin Shop</p>
          <p className="text-[11px] text-slate-400">Buy more Jixxeo Coins</p>
        </Link>
        <Link to="/coins" data-testid="profile-elite-cta" className="jx-glass rounded-2xl p-3 active:scale-95">
          <Crown size={20} className="text-amber-300" />
          <p className="mt-1 font-heading font-semibold text-sm">One Tap Elite</p>
          <p className="text-[11px] text-slate-400">Unlimited scans · $4.99/mo</p>
        </Link>
      </div>

      {/* Tip jar card */}
      <button
        data-testid="profile-tip-cta"
        onClick={() => setTipOpen(true)}
        className="mt-2 w-full jx-glass rounded-2xl p-3 flex items-center gap-3 active:scale-95 border border-rose-400/20 text-left"
      >
        <div className="w-10 h-10 rounded-xl bg-rose-500/15 border border-rose-400/40 text-rose-300 flex items-center justify-center">
          <Heart size={18} fill="currentColor" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-semibold text-sm leading-tight">Leave a tip</p>
          <p className="text-[11px] text-slate-400 leading-tight">Help keep Jixxeo Scan free and ad-free</p>
        </div>
        <span className="text-[10px] font-mono-data uppercase text-rose-300">Tip us</span>
      </button>

      {/* Folders */}
      <div className="mt-4 jx-glass rounded-3xl p-4">
        <div className="flex items-center gap-2"><Folder size={16} className="text-emerald-300" /><h3 className="font-heading font-semibold">Scan folders</h3></div>
        <p className="text-[11px] text-slate-400 mt-1">Organise saved scans into collections. Default: Garden, Travel, Fix-it.</p>
        {loadingCols ? (
          <div className="py-4 flex justify-center"><Loader2 className="animate-spin text-slate-500" size={18} /></div>
        ) : (
          <div className="mt-3 grid grid-cols-2 gap-2">
            {cols.map((c) => (
              <Link
                key={c.id}
                to={`/collections/${c.id}`}
                data-testid={`folder-${c.id}`}
                className="relative rounded-2xl bg-white/5 border border-white/10 p-3 active:scale-95 overflow-hidden"
                style={{ borderColor: `${c.color}55` }}
              >
                <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full blur-2xl opacity-25" style={{ background: c.color }} />
                <div className="relative flex items-center gap-2">
                  <div className="text-2xl">{c.emoji || "📁"}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-semibold text-sm truncate">{c.name}</p>
                    <p className="text-[10px] font-mono-data uppercase text-slate-500">{c.count} scan{c.count === 1 ? "" : "s"}</p>
                  </div>
                  <button
                    data-testid={`folder-del-${c.id}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeFolder(c.id); }}
                    className="text-slate-500 hover:text-rose-400 p-1"
                    aria-label="Delete folder"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </Link>
            ))}
          </div>
        )}
        <form onSubmit={createFolder} className="mt-3 flex items-center gap-2">
          <input
            data-testid="folder-new-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New folder name"
            className="flex-1 h-10 px-3 rounded-xl bg-black/30 border border-white/10 outline-none focus:border-cyan-400/60 text-sm"
          />
          <button data-testid="folder-new-submit" type="submit"
            className="h-10 px-3 rounded-xl bg-cyan-400 text-slate-950 font-semibold text-sm active:scale-95">
            Create
          </button>
        </form>
      </div>

      <div className="mt-4 jx-glass rounded-3xl p-4">
        <div className="flex items-center gap-2"><Trophy size={16} className="text-cyan-300" /><h3 className="font-heading font-semibold">Badges</h3></div>
        <div className="mt-3 grid grid-cols-4 gap-2">
          {BADGES.map((b) => (
            <div key={b.id} className="text-center rounded-xl bg-white/5 border border-white/10 p-2">
              <div className="text-2xl">{b.icon}</div>
              <p className="text-[10px] font-heading font-semibold mt-0.5">{b.title}</p>
              <p className="text-[9px] text-slate-500">{b.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 jx-glass rounded-3xl p-4">
        <div className="flex items-center gap-2 text-slate-300"><Flame size={16} className="text-rose-400" /><span className="font-heading font-semibold">Daily streak</span></div>
        <p className="text-sm text-slate-400 mt-1">Scan something new every day to keep your streak alive. Current: <span className="text-white font-mono-data">{user.streak || 0}</span></p>
      </div>

      <button data-testid="profile-logout" onClick={logout} className="mt-4 w-full h-12 rounded-2xl border border-white/10 text-slate-300 flex items-center justify-center gap-2 active:scale-95">
        <LogOut size={16} /> Log out
      </button>
      <TipJar open={tipOpen} onClose={() => setTipOpen(false)} />
    </div>
  );
}
